import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationVariants } from '@common/components';
import { COMMON_TRANSLATION } from '@common/constants';
import { useAuth } from '@modules/auth/hooks';
import { useAuthStore, UserSettingsState } from '@modules/auth/store';
import { TermsOfServiceModal, TermsOfServiceUnavailableModal } from '@modules/core/components';
import { useMutationUserSettings, useQueryUserSettings } from '@modules/core/hooks';
import { UserSettings as UserSettingsType, UserSettingsContext, UserSettingsErrors } from '@modules/core/types';
import { useAddNotification } from '@modules/notifications/store';
import { UserSettingsProps } from './UserSettings.types';

const UserSettings: FC<UserSettingsProps> = ({ children }) => {
  const addNotification = useAddNotification();
  const { authService, isAuthenticated } = useAuth();
  const {
    logout: authStoreLogout,
    user: currentUser,
    settings: userSettings,
    updateIsLoggingOut,
    updateSettings,
  } = useAuthStore();
  const { t } = useTranslation(COMMON_TRANSLATION);

  const [isUserSettingsNew, setIsUserSettingsNew] = useState<boolean>();
  const [isUserSettingsUnavailable, setIsUserSettingsUnavailable] = useState<boolean>(false);

  const { refetch: fetchUserSettings } = useQueryUserSettings(
    {
      userId: currentUser?.id ?? '',
      onError: handleGetUserSettingsError,
      onSuccess: handleGetUserSettingsSuccess,
    },
    {
      enabled: false,
    },
  );
  const { mutateAsync: mutateUserSettings, isLoading } = useMutationUserSettings(
    handleMutateUserSettingsSuccess,
    handleMutateUserSettingsError,
  );

  function handleGetUserSettingsError(error: unknown): void {
    if ((error as Error).message === UserSettingsErrors.NOT_FOUND) {
      setIsUserSettingsNew(true);
      const settings: UserSettingsState = { acceptedTerms: false };

      settings &&
        updateSettings({
          ...settings,
        });
    } else {
      addNotification({
        title: t('terms_of_service.errors.fetch'),
        type: NotificationVariants.ERROR,
      });
      setIsUserSettingsUnavailable(true);
    }
  }

  function handleGetUserSettingsSuccess(data: UserSettingsType | undefined): void {
    setIsUserSettingsNew(false);
    const settings: UserSettingsState = { acceptedTerms: !!(data as UserSettingsType)?.acceptedTerms };

    settings &&
      updateSettings({
        ...settings,
      });
    setIsUserSettingsUnavailable(false);
  }

  function handleMutateUserSettingsSuccess(): void {
    addNotification({
      autoClose: true,
      title: t('terms_of_service.success'),
      type: NotificationVariants.SUCCESS,
    });

    userSettings &&
      updateSettings({
        ...userSettings,
        acceptedTerms: true,
      });
    setIsUserSettingsUnavailable(false);
  }

  function handleMutateUserSettingsError(
    _error: unknown,
    _variables: Partial<UserSettingsType>,
    _context: UserSettingsContext | undefined,
  ): void {
    addNotification({
      autoClose: true,
      title: t('terms_of_service.errors.update'),
      type: NotificationVariants.ERROR,
    });
    setIsUserSettingsUnavailable(true);
  }

  const handleSubmit = (): void => {
    userSettings &&
      mutateUserSettings({
        isNew: isUserSettingsNew,
        acceptedTerms: true,
        userId: currentUser?.id,
      }).catch(() => {});
  };

  const handleLogout = async (): Promise<void> => {
    updateIsLoggingOut(true);
    await authService
      .signOut()
      .then(() => {
        authStoreLogout();
      })
      .catch((error) => {
        updateIsLoggingOut(false);
        throw new Error(error);
      });
  };

  const handleRetry = (): void => {
    // if userSettings is available then it mains what is failing is to update ToS.
    if (userSettings) {
      handleSubmit();
    } else {
      void fetchUserSettings();
    }
  };

  useEffect(() => {
    if (isAuthenticated === true && !!currentUser) {
      void fetchUserSettings();
    }
  }, [isAuthenticated, currentUser]);

  return (
    <>
      {userSettings && (
        <TermsOfServiceModal
          isLoading={isLoading}
          isOpen={!userSettings.acceptedTerms}
          onClose={handleSubmit}
          readOnly={false}
        />
      )}
      {isUserSettingsUnavailable && (
        <TermsOfServiceUnavailableModal
          isOpen={isUserSettingsUnavailable}
          onLogout={handleLogout}
          onRetry={handleRetry}
        />
      )}
      {userSettings?.acceptedTerms && children}
    </>
  );
};

export default UserSettings;
